import { useForm, useWatchEntityUpdates } from '@domain-group/react-hooks'

import type { AgencyModel, CustomFieldModel } from '@models/types'

import useEntityCustomData from './useEntityCustomData'
import useEntityTypeCustomFields from './useEntityTypeCustomFields'

const watchEntityKeys = [
  'customFields',
  'dataStoreItems',
]

type FindCustomFieldForKeyParams = {
  customFieldKey: string,
  customFieldsArray: CustomFieldModel[],
}

const findCustomFieldForKey = (params: FindCustomFieldForKeyParams) => {
  const { customFieldKey, customFieldsArray } = params

  const customField = customFieldsArray.find((item) => {
    const { key } = item
    return key === customFieldKey
  })

  return customField || {}
}

type BuildNestedValuesParams = {
  customFieldsArray: CustomFieldModel[],
  entityState: { [key: string]: string | number },
}

const buildNestedValues = (params: BuildNestedValuesParams) => {
  const { customFieldsArray, entityState } = params

  const keys = Object.keys(entityState)

  const nestedValues = keys.map((key) => {
    const customField = findCustomFieldForKey({ customFieldsArray, customFieldKey: key })

    return {
      // @ts-expect-error TS2339
      custom_field_id: customField.id,
      data_store_item_value: entityState[key] || '',
    }
  })

  return nestedValues
}

type UseEntityCustomFieldsFormParams = {
  entity: {
    id: number,
    type: string,
  },
  options: {
    agency: AgencyModel,
    hidden?: boolean,
    performHttpRequests?: boolean,
  },
  deps?: any,
}

const useEntityCustomFieldsForm = (params: UseEntityCustomFieldsFormParams) => {
  const { entity, options, deps = [] } = params
  const {
    agency, hidden, performHttpRequests,
  } = options || {}

  const {
    updatedEntities: {
      customFields: customFieldsUpdatedAt,
      dataStoreItems: dataStoreItemsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entityCustomFieldsPayload = useEntityTypeCustomFields({
    entityType: entity.type,
    options: {
      agency,
      hidden,
      performHttpRequests,
    },
    deps,
  })

  const { filteredCustomFields, requiredFields } = entityCustomFieldsPayload

  // Setup default state
  const customDataPayload = useEntityCustomData({ entity, filteredCustomFields })
  const { customData } = customDataPayload

  const formPayload = useForm(customData, { entity: customData, requiredFields }, [
    entity.type,
    entity.id,
    customFieldsUpdatedAt,
    dataStoreItemsUpdatedAt,
  ])

  const { entityState } = formPayload

  return {
    callbacks: {
      buildNestedValues: () => buildNestedValues({ customFieldsArray: filteredCustomFields, entityState }),
    },
    ...entityCustomFieldsPayload,
    ...formPayload,
    requiredFields,
  }
}

export type UseEntityCustomFieldsFormPayload = ReturnType<typeof useEntityCustomFieldsForm>

export default useEntityCustomFieldsForm
